"use client";

import { useRouter } from "@bprogress/next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";

import SocketEventEnum from "@/constants/socket-event";
import { useCallActions } from "@/hooks/use-call-store";
import { useConversationStore } from "@/hooks/use-conversation-store";
import { useSocketStore } from "@/hooks/use-socket";
import { readMessage } from "@/lib/apis/conversation";
import { showError } from "@/lib/toast";
import { addedConversation, addLastMessage, addMessageConversation, addMessageConversationLiveStream, addMessageStatusToMessage, addUnReadCount, removeConversation, removeMessageReactionInConversation, toggleMessageDeleteState, updateMessageContentInConversation, updateMessageReactionInConversation, updateParticipantCount } from "@/lib/update-conversation";
import updatePostInQueryData, { addCommentReactionToPost, addCommentToPost, addReplyCommentToPost, deleteCommentToPost, editCommentToPost, incrementDecrementCommentCount } from "@/lib/update-post-data";
import { addCommentToShort, incrementDecrementShortCommentCount, updateShortInQueryData } from "@/lib/update-stream-data";
import { useStore } from "@/store/store";

function SocketEvents() {
  const { socket } = useSocketStore();
  const queryClient = useQueryClient();
  const { user, sessionId: currentSessionId, selectedConversation, setSelectedConversation, markUserOffline, markUserOnline, updateLastSeen, setUser, addNotification, logout } = useStore();
  const { setIncomingCall, setRejectedCallInfo, stopRingtone, setCallStatus, endCall, setShowEndModal, setHostUsername } = useCallActions();
  const { incrementUnreadCount } = useConversationStore();
  const router = useRouter();

  const { mutate: readMessageFun } = useMutation({
    mutationFn: readMessage,
    onError: (error) => {
      showError(error.message);
    },
  });

  useEffect(() => {
    if (!socket)
      return;

    socket.on(SocketEventEnum.POST_REACT, (payload: PostReactPayload) => {
      queryClient.setQueryData(["get_all_posts"], (oldData: QueryOldDataPayload) => {
        return updatePostInQueryData(oldData, payload.postId, payload.postData);
      });
    });

    return () => {
      socket.off(SocketEventEnum.POST_REACT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;

    socket.on(SocketEventEnum.CREATE_SHORT_COMMENT, (payload: CreateCommentPayload) => {
      queryClient.setQueryData(["get_comments", payload?.data?.shortId], (oldData: QueryOldDataCommentsPayload) => {
        return addCommentToShort(oldData, payload?.data?.shortId, payload.data);
      });
      queryClient.setQueryData(["get_shorts"], (oldData: QueryOldShortsDataPayload) => {
        return incrementDecrementShortCommentCount(oldData, payload?.data?.shortId, 1, "inc");
      });
    });

    return () => {
      socket.off(SocketEventEnum.CREATE_SHORT_COMMENT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;

    socket.on(SocketEventEnum.CREATE_COMMENT, (payload: CreateCommentPayload) => {
      queryClient.setQueryData(["get_comments", payload?.data?.postId], (oldData: QueryOldDataCommentsPayload) => {
        return addCommentToPost(oldData, payload?.data?.postId, payload.data);
      });
      queryClient.setQueryData(["get_all_posts"], (oldData: QueryOldDataPayload) => {
        return incrementDecrementCommentCount(oldData, payload?.data?.postId, 1, "inc");
      });
    });

    return () => {
      socket.off(SocketEventEnum.CREATE_COMMENT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;

    socket.on(SocketEventEnum.SHORT_REPLY_COMMENT, (payload: CreateCommentReplyPayload) => {
      queryClient.setQueryData(["get_comments", payload?.data?.shortId], (oldData: QueryOldDataCommentsPayload) => {
        return addReplyCommentToPost(oldData, payload.data.parentId, payload.data);
      });

      queryClient.setQueryData(["get_shorts"], (oldData: QueryOldShortsDataPayload) => {
        return incrementDecrementShortCommentCount(oldData, payload?.data?.shortId, 1, "inc");
      });
    });

    return () => {
      socket.off(SocketEventEnum.SHORT_REPLY_COMMENT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;

    socket.on(SocketEventEnum.REPLY_COMMENT, (payload: CreateCommentReplyPayload) => {
      queryClient.setQueryData(["get_comments", payload?.data?.postId], (oldData: QueryOldDataCommentsPayload) => {
        return addReplyCommentToPost(oldData, payload.data.parentId, payload.data);
      });

      queryClient.setQueryData(["get_all_posts"], (oldData: QueryOldDataPayload) => {
        return incrementDecrementCommentCount(oldData, payload?.data?.postId, 1, "inc");
      });
    });

    return () => {
      socket.off(SocketEventEnum.REPLY_COMMENT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;

    socket.on(SocketEventEnum.COMMENT_REACT, (payload: CommentReactPayload) => {
      queryClient.setQueryData(["get_comments", payload?.postId], (oldData: QueryOldDataCommentsPayload) => {
        return addCommentReactionToPost(oldData, payload?.commentId, payload?.parentId, payload?.isReply, payload?.data?.totalReactionsCount ?? 0);
      });
    });

    return () => {
      socket.off(SocketEventEnum.COMMENT_REACT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.SHORT_EDITED_COMMENT, (payload: UpdatedCommentPayload) => {
      queryClient.setQueryData(["get_comments", payload?.postId], (oldData: QueryOldDataCommentsPayload) => {
        return editCommentToPost(oldData, payload.id, payload);
      });
    });
    return () => {
      socket.off(SocketEventEnum.SHORT_EDITED_COMMENT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.EDITED_COMMENT, (payload: UpdatedCommentPayload) => {
      queryClient.setQueryData(["get_comments", payload?.postId], (oldData: QueryOldDataCommentsPayload) => {
        return editCommentToPost(oldData, payload.id, payload);
      });
    });
    return () => {
      socket.off(SocketEventEnum.EDITED_COMMENT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.SHORT_DELETE_COMMENT, (payload: DeleteCommentPayload) => {
      queryClient.setQueryData(["get_comments", payload?.shortId], (oldData: QueryOldDataCommentsPayload) => {
        if (!oldData)
          return { pageParams: [], pages: [] };
        return deleteCommentToPost(oldData, payload.id, payload.parentId, payload.isReply);
      });
      queryClient.setQueryData(["get_shorts"], (oldData: QueryOldShortsDataPayload) => {
        return incrementDecrementShortCommentCount(oldData, payload?.shortId, 1, "dec");
      });
    });
    return () => {
      socket.off(SocketEventEnum.SHORT_DELETE_COMMENT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.DELETE_COMMENT, (payload: DeleteCommentPayload) => {
      queryClient.setQueryData(["get_comments", payload?.postId], (oldData: QueryOldDataCommentsPayload) => {
        if (!oldData)
          return { pageParams: [], pages: [] };
        return deleteCommentToPost(oldData, payload.id, payload.parentId, payload.isReply);
      });
      queryClient.setQueryData(["get_all_posts"], (oldData: QueryOldDataPayload) => {
        return incrementDecrementCommentCount(oldData, payload?.postId, 1, "dec");
      });
    });
    return () => {
      socket.off(SocketEventEnum.DELETE_COMMENT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.SEND_MESSAGE_TO_CONVERSATION, (payload: ConversationMessages) => {
      if (payload?.senderId !== user?.id) {
        queryClient.setQueryData(["get_conversation_messages", payload.conversationId], (oldData: QueryOldDataPayloadConversation) => {
          if (selectedConversation?.conversationId === payload.conversationId && payload.senderId !== user?.id) {
            readMessageFun({
              conversationId: payload.conversationId,
              messageId: payload.id,
            });
          }
          return addMessageConversation(oldData, payload, payload.conversationId);
        });
        if (selectedConversation?.conversationId !== payload.conversationId) {
          queryClient.setQueryData(["get_conversations"], (oldData: QueryOldDataPayloadConversations) => {
            return addUnReadCount(oldData, payload.conversationId);
          });
          incrementUnreadCount(payload.conversationId);
        }
      }

      queryClient.setQueryData(["get_conversations"], (oldData: QueryOldDataPayloadConversations) => {
        return addLastMessage(oldData, payload.conversationId, payload);
      });
    });
    return () => {
      socket.off(SocketEventEnum.SEND_MESSAGE_TO_CONVERSATION);
    };
  }, [socket, user, selectedConversation]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.SEND_CONVERSATION_REQUEST, (payload: Conversation) => {
      queryClient.setQueryData(["get_conversations"], (oldData: QueryOldDataPayloadConversations) => {
        return addedConversation(oldData, payload, payload?.id);
      });
    });
    return () => {
      socket.off(SocketEventEnum.SEND_CONVERSATION_REQUEST);
    };
  }, [socket, user]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.ONLINE, ({ userId }: { userId: number }) => {
      markUserOnline(userId);
    });
    return () => {
      socket.off(SocketEventEnum.ONLINE);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.OFFLINE, ({ userId, lastSeen }: { userId: number; lastSeen: number }) => {
      markUserOffline(userId);
      updateLastSeen(userId, lastSeen);
    });
    return () => {
      socket.off(SocketEventEnum.OFFLINE);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.INCOMING_CALL, (payload) => {
      setIncomingCall(payload);
    });
    return () => {
      socket.off(SocketEventEnum.INCOMING_CALL);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.CALL_ACCEPTED, ({ callType, authToken, roomName }) => {
      stopRingtone();
      router.replace(`/call?room=${roomName}&type=${callType}&authToken=${authToken}`, { showProgress: true });
    });
    return () => {
      socket.off(SocketEventEnum.CALL_ACCEPTED);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.CALL_REJECTED, (payload) => {
      setRejectedCallInfo(payload);
      router.push("/", { showProgress: true });
    });
    return () => {
      socket.off(SocketEventEnum.CALL_REJECTED);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.CALLER_LEFT, (payload) => {
      setRejectedCallInfo(payload);
      setCallStatus("ended");
      endCall();
      router.push("/", { showProgress: true });
    });
    return () => {
      socket.off(SocketEventEnum.CALLER_LEFT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.CALL_TIMEOUT, (payload) => {
      endCall();
      setRejectedCallInfo(payload);
      setCallStatus("missed");
      router.push("/", { showProgress: true });
    });
    return () => {
      socket.off(SocketEventEnum.CALL_TIMEOUT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.HOST_END_LIVE_STREAM, (payload) => {
      setShowEndModal(true);
      setHostUsername(payload?.username);
      router.push("/", { showProgress: true });
    });
    return () => {
      socket.off(SocketEventEnum.HOST_END_LIVE_STREAM);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.LIVE_CHAT_SEND, (payload: LiveStreamChatData) => {
      if (payload?.senderId !== user?.id) {
        queryClient.setQueryData(["get_stream_messages"], (oldData: QueryOldDataPayloadLiveStreamChats) => {
          return addMessageConversationLiveStream(oldData, payload, payload?.streamId);
        });
      }
    });
    return () => {
      socket.off(SocketEventEnum.LIVE_CHAT_SEND);
    };
  }, [socket, user]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.LIVE_VIEW_COUNT, (payload) => {
      queryClient.setQueryData(["get_streams"], (oldData: LiveStreamResponse) => {
        return updateParticipantCount(oldData, Number(payload?.streamId), payload?.count ?? 0);
      });
    });
    return () => {
      socket.off(SocketEventEnum.LIVE_VIEW_COUNT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.USER_KICK_FROM_LIVE, () => {
      router.refresh();
    });
    return () => {
      socket.off(SocketEventEnum.USER_KICK_FROM_LIVE);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.BAN_VIEWER_FROM_MY_LIVE_STREAM, () => {
      router.refresh();
    });
    return () => {
      socket.off(SocketEventEnum.BAN_VIEWER_FROM_MY_LIVE_STREAM);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.NOTIFY_USER, (payload: NotificationsEntity) => {
      addNotification(payload);
    });
    return () => {
      socket.off(SocketEventEnum.NOTIFY_USER);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.FOLLOW_USER, () => {
      if (user) {
        setUser({
          ...user,
          followersCount: user.followersCount + 1,
        });
      }
    });
    return () => {
      socket.off(SocketEventEnum.FOLLOW_USER);
    };
  }, [socket, user]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.UNFOLLOW_USER, () => {
      if (user) {
        setUser({
          ...user,
          followersCount: Math.max(0, user.followersCount - 1),
        });
      }
    });
    return () => {
      socket.off(SocketEventEnum.UNFOLLOW_USER);
    };
  }, [socket, user]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.DELETE_CONVERSATION, ({ conversationId }) => {
      if (selectedConversation?.conversationId === conversationId) {
        setSelectedConversation(null);
      }
      queryClient.setQueryData(["get_conversations"], (oldData: QueryOldDataPayloadConversations) => {
        return removeConversation(oldData, conversationId);
      });
    });
    return () => {
      socket.off(SocketEventEnum.DELETE_CONVERSATION);
    };
  }, [socket, selectedConversation]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.REACT_CONVERSATION_MESSAGE, (payload: MessageReaction) => {
      queryClient.setQueryData(["get_conversation_messages", payload.conversationId], (oldData: QueryOldDataPayloadConversation) => {
        return updateMessageReactionInConversation(oldData, payload.conversationId ?? 0, payload.messageId, payload);
      });
    });
    return () => {
      socket.off(SocketEventEnum.REACT_CONVERSATION_MESSAGE);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.REMOVE_REACT_CONVERSATION_MESSAGE, ({ messageId, userId, conversationId }) => {
      queryClient.setQueryData(["get_conversation_messages", conversationId], (oldData: QueryOldDataPayloadConversation) => {
        return removeMessageReactionInConversation(oldData, conversationId, messageId, userId);
      });
    });
    return () => {
      socket.off(SocketEventEnum.REMOVE_REACT_CONVERSATION_MESSAGE);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.EDITED_CONVERSATION, (payload) => {
      queryClient.setQueryData(["get_conversation_messages", payload.conversationId], (oldData: QueryOldDataPayloadConversation) => {
        return updateMessageContentInConversation(oldData, payload.conversationId, payload.id, payload.content, payload.keyForRecipient, payload.keyForSender);
      });
    });
    return () => {
      socket.off(SocketEventEnum.EDITED_CONVERSATION);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.DELETE_CONVERSATION_MESSAGE, (payload) => {
      queryClient.setQueryData(["get_conversation_messages", payload.conversationId], (oldData: QueryOldDataPayloadConversation) => {
        return toggleMessageDeleteState(oldData, payload.conversationId, payload.id, true);
      });
    });
    return () => {
      socket.off(SocketEventEnum.DELETE_CONVERSATION_MESSAGE);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.UNDO_DELETE_CONVERSATION_MESSAGE, (payload) => {
      queryClient.setQueryData(["get_conversation_messages", payload.conversationId], (oldData: QueryOldDataPayloadConversation) => {
        return toggleMessageDeleteState(oldData, payload.conversationId, payload.id, false);
      });
    });
    return () => {
      socket.off(SocketEventEnum.UNDO_DELETE_CONVERSATION_MESSAGE);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.READ_MESSAGE, ({ conversationId, messageId, status, tempId }) => {
      queryClient.setQueryData(["get_conversation_messages", conversationId], (oldData: QueryOldDataPayloadConversation) => {
        return addMessageStatusToMessage(oldData, conversationId, tempId || messageId, status);
      });
    });
    return () => {
      socket.off(SocketEventEnum.READ_MESSAGE);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.SHORT_REACT, ({ postData, postId }) => {
      queryClient.setQueryData(["get_shorts"], (oldData: QueryOldShortsDataPayload) =>
        updateShortInQueryData(oldData, postId, postData.currentUserReaction, postData?.totalReactionsCount));
    });
    return () => {
      socket.off(SocketEventEnum.SHORT_REACT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.FORCE_LOGOUT, async (payload) => {
      if (Number(payload) === Number(currentSessionId)) {
        await logout();
        router.replace("/login", { showProgress: true });
      }
    });
    return () => {
      socket.off(SocketEventEnum.FORCE_LOGOUT);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.POST_COMPLETED, async (payload) => {
      const postUrl = `/posts/${payload.postId}`;
      toast.success("Your post is now live", {
        position: "top-right",
        action: {
          label: "View",
          onClick: () => router.push(postUrl, { showProgress: true }),
        },
      });
    });
    return () => {
      socket.off(SocketEventEnum.POST_COMPLETED);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.POST_FAILED, async ({ reason }) => {
      showError(reason);
    });
    return () => {
      socket.off(SocketEventEnum.POST_FAILED);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.POST_UPDATE_COMPLETED, async (data) => {
      queryClient.setQueryData(["get_all_posts"], (oldData: QueryOldDataPayload) => {
        const updatedPages = oldData?.pages?.map((page) => {
          const updatedPost = page?.data?.posts?.map((post) => {
            if (post.id === data.id) {
              return {
                ...post,
                ...data,
              };
            }

            return post;
          });

          return {
            ...page,
            data: {
              ...page.data,
              posts: updatedPost,
            },
          };
        });

        return {
          ...oldData,
          pages: updatedPages,
        };
      });
      toast.success("Update Post Successfully", {
        position: "top-right",
        action: {
          label: "View",
          onClick: () => router.push(`/posts/${data.id}`, { showProgress: true }),
        },
      });
    });
    return () => {
      socket.off(SocketEventEnum.POST_UPDATE_COMPLETED);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket)
      return;
    socket.on(SocketEventEnum.POST_UPDATE_FAILED, async ({ reason }) => {
      showError(reason);
    });
    return () => {
      socket.off(SocketEventEnum.POST_UPDATE_FAILED);
    };
  }, [socket]);

  return null;
}

export default SocketEvents;
